import { forwardRef, useEffect, useImperativeHandle, useRef, useState, useCallback } from 'react';
import { GoogleMap } from '@capacitor/google-maps';
import { Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAddressAutocomplete, useGeocode, useReverseGeocode } from '../../hooks/useMaps';
import type { Address, AddressSuggestion } from '../../types/address';
import {
  BASE_MAP_CONFIG,
  dropoffRadiusCircle,
  getBaseMapLocale,
  zoomToFitRadius,
} from '../../utils/mapConfig';
import { AddressEditCard } from './AddressEditCard';
import { distanceMiles } from '../../utils/geoDistance';

export interface LocationPickerValue {
  address: Address;
  displayText: string;
}

export interface LocationPickerHandle {
  /** Geolocate + reverse-geocode, then emit via onChange. Rejects with a user-facing message on failure. */
  useCurrentLocation: () => Promise<void>;
}

type LocationConflict = {
  profile: LocationPickerValue;
  device: LocationPickerValue;
};

interface Props {
  /** Current selected location. */
  value?: LocationPickerValue | null;
  /** Called when the user picks / confirms a location. */
  onChange: (location: LocationPickerValue) => void;
  /** Profile address (type === 'profile') if one exists. */
  profileAddress?: Address | null;
  /**
   * Emits whether the apt/unit prompt has been resolved — either by entering
   * a unit or by the user explicitly clicking "None". Parents use this to
   * gate publish.
   */
  onAptResolutionChange?: (resolved: boolean) => void;
  /**
   * Emits whether the pin-confirmation prompt has been resolved — either by
   * the address having a real street1 or by the user explicitly confirming
   * a pin-only location. Parents use this to gate publish.
   */
  onPinResolutionChange?: (resolved: boolean) => void;
  /**
   * When true, the map and the pin-confirm prompt are shown only for a real
   * street address — a ZIP / area-level location (no street1) renders as a
   * plain text row with no map and no "confirm pin" step. Used by the profile
   * editor, where a ZIP-only address is a valid minimum and the map/pin are
   * refinements that only make sense for a precise street address.
   */
  hideMapAndPinForZip?: boolean;
  /**
   * When false, the mount-time initialisation below is skipped entirely — no
   * profile seed, and crucially no `navigator.geolocation` call.
   *
   * The order flow needs this: an eater browsing by typed ZIP has said not to
   * use their position, and silently geolocating them to fill a DELIVERY
   * address contradicts that. Defaults true so the publish and profile
   * surfaces, which do want the suggestion, are unchanged.
   */
  autoSeed?: boolean;
  /**
   * Drop-off radius in miles to preview around the pin, so a chef can see the
   * area they're agreeing to drive. When set, the map zooms to fit the whole
   * circle instead of the usual street-level zoom, and the circle follows the
   * pin and resizes as the radius changes. Omitted everywhere but the publish
   * form's drop-off listings.
   */
  radiusMiles?: number | null;
}

const DEFAULT_CENTER = { lat: 37.7749, lng: -122.4194 };

// Tight tolerance — catches "publishing from a different location than your
// saved profile address" while tolerating typical browser-geolocation jitter
// (often ±50-200ft, sometimes much worse on desktop where it's IP-based).
const MISMATCH_THRESHOLD_FEET = 500;
const MISMATCH_THRESHOLD_MILES = MISMATCH_THRESHOLD_FEET / 5280;

export const LocationPicker = forwardRef<LocationPickerHandle, Props>(function LocationPicker({
  value,
  onChange,
  profileAddress,
  onAptResolutionChange,
  onPinResolutionChange,
  hideMapAndPinForZip = false,
  autoSeed = true,
  radiusMiles = null,
}, ref) {
  const { t } = useTranslation('location');
  const mapRef = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<GoogleMap | null>(null);
  const markerIdRef = useRef<string | null>(null);
  const circleIdsRef = useRef<string[]>([]);
  const radiusRef = useRef(radiusMiles);
  radiusRef.current = radiusMiles;
  const initRef = useRef(false);

  const valueRef = useRef(value);
  valueRef.current = value;

  const [searchText, setSearchText] = useState('');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [sessionToken, setSessionToken] = useState<string | undefined>();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchWrapperRef = useRef<HTMLDivElement>(null);

  const [conflict, setConflict] = useState<LocationConflict | null>(null);

  const [apt2, setApt2] = useState<string>(value?.address.street2 ?? '');
  const apt2DebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Inline edit-mode card: when true, the read-only address row is replaced
  // by <AddressEditCard>. Reset on cancel/save.
  const [isEditing, setIsEditing] = useState(false);

  // Entering address-edit mode counts as the user resolving the mismatch
  // (they're going to fix it themselves). Dismiss the conflict prompt so it
  // doesn't sit there alongside the edit card.
  useEffect(() => {
    if (isEditing) setConflict(null);
  }, [isEditing]);

  // Defer map creation until we have real coords. Without this gate the map
  // briefly renders with the default (San Francisco) center while profile /
  // device location is still resolving. Latches true once flipped — we never
  // hide the map again if `value` is later cleared.
  const [showMap, setShowMap] = useState<boolean>(
    !!(value?.address.lat && value?.address.lon),
  );
  useEffect(() => {
    if (showMap) return;
    if (value?.address.lat && value?.address.lon) setShowMap(true);
  }, [showMap, value?.address.lat, value?.address.lon]);

  // In zip-friendly mode the map is only relevant for a real street address, so
  // it's gated on street1 (and reactively hidden if the value drops back to a
  // ZIP) rather than using the latched `showMap` — otherwise a device-autofill
  // on open would keep the map pinned even after the user types a ZIP.
  const mapVisible = hideMapAndPinForZip
    ? !!(value?.address.street1 && value?.address.lat != null && value?.address.lon != null)
    : showMap;

  // Reset apt2 when the underlying address identity changes (new pick, map pin
  // move, conflict resolution). Only depends on identity fields, not street2
  // itself, so the user's in-flight typing isn't clobbered by our own debounced
  // emits. (`noUnit` lives on `value.address` now, so it's reset automatically
  // when the parent emits a new address.)
  useEffect(() => {
    setApt2(value?.address.street2 ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.address.placeId, value?.address.street1, value?.address.lat, value?.address.lon]);

  // Emit apt resolution status to the parent. Resolved iff there's no street1
  // yet (not applicable), OR street2 is set, OR the user explicitly confirmed
  // no unit (persisted on the address).
  const aptResolved =
    !value?.address.street1 || !!value?.address.street2 || !!value?.address.noUnit;
  useEffect(() => {
    onAptResolutionChange?.(aptResolved);
  }, [aptResolved, onAptResolutionChange]);

  // Emit pin resolution status to the parent. Resolved iff there's no value
  // yet (not applicable), OR the address has a real street1, OR the user
  // explicitly confirmed the pin location (persisted on the address).
  const pinResolved =
    !value ||
    !!value.address.street1 ||
    !!value.address.pinConfirmed;
  useEffect(() => {
    onPinResolutionChange?.(pinResolved);
  }, [pinResolved, onPinResolutionChange]);

  // Mirror the current selection into the search bar so the user sees the
  // resolved address (and apt number) instead of an empty input post-pick.
  useEffect(() => {
    setSearchText(value?.displayText ?? '');
  }, [value?.displayText]);

  // Dismiss the suggestions dropdown when the user clicks outside it.
  useEffect(() => {
    if (suggestions.length === 0) return;
    function handleMouseDown(e: MouseEvent) {
      if (!searchWrapperRef.current) return;
      if (!searchWrapperRef.current.contains(e.target as Node)) {
        setSuggestions([]);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [suggestions.length]);

  // --- TanStack Query hooks ---
  const autocompleteMutation = useAddressAutocomplete();
  const geocodeMutation = useGeocode();
  const reverseGeocodeMutation = useReverseGeocode();

  // Stable ref so map event listeners always call the latest mutateAsync
  const reverseGeocodeRef = useRef(reverseGeocodeMutation.mutateAsync);
  reverseGeocodeRef.current = reverseGeocodeMutation.mutateAsync;

  // --- Helpers: marker + camera ---

  /** Street-level zoom, or wide enough to show the whole drop-off circle. */
  function zoomFor(lat: number) {
    const miles = radiusRef.current;
    const el = mapRef.current;
    if (!miles || !el) return 15;
    return zoomToFitRadius(lat, miles, el.clientWidth, el.clientHeight);
  }

  /** (Re)draw the drop-off circle around the pin; clears it when no radius. */
  async function drawRadius(map: GoogleMap, lat: number, lng: number) {
    if (circleIdsRef.current.length) {
      try {
        await map.removeCircles(circleIdsRef.current);
      } catch {
        /* may already be gone */
      }
      circleIdsRef.current = [];
    }
    const miles = radiusRef.current;
    if (miles) {
      circleIdsRef.current = await map.addCircles([dropoffRadiusCircle({ lat, lng }, miles)]);
    }
  }

  async function syncMapToCoords(map: GoogleMap, lat: number, lng: number) {
    await map.setCamera({ coordinate: { lat, lng }, zoom: zoomFor(lat), animate: true });
    if (markerIdRef.current) {
      try {
        await map.removeMarker(markerIdRef.current);
      } catch {
        /* may already be gone */
      }
    }
    const id = await map.addMarker({
      coordinate: { lat, lng },
      draggable: true,
    });
    markerIdRef.current = id;
    await drawRadius(map, lat, lng);
  }

  // Stable ref for onChange so map listeners don't go stale
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  /** Reverse-geocode a lat/lon and call onChange. Used by map click + drag. */
  const handleMapPick = useCallback(async (latitude: number, longitude: number, map?: GoogleMap) => {
    // Picking a new spot on the map counts as resolving the profile/device
    // mismatch — the user has made an explicit choice.
    setConflict(null);
    if (map) await syncMapToCoords(map, latitude, longitude);

    try {
      const addr = await reverseGeocodeRef.current({ lat: latitude, lon: longitude });
      // Always preserve the user's actual pin coords. Google's geocoder
      // returns the centroid of whatever address it matched, which can be
      // miles from the pin when the pin is in a non-addressable area.
      addr.lat = latitude;
      addr.lon = longitude;
      // New pin position → user must re-confirm if the result lacks a street.
      addr.pinConfirmed = false;
      onChangeRef.current({ address: addr, displayText: formatAddress(addr) });
    } catch (err) {
      console.error('[LocationPicker] reverse geocode failed', err);
      onChangeRef.current({
        address: { lat: latitude, lon: longitude, pinConfirmed: false },
        displayText: `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Initialisation: profile → geolocation → conflict check ---
  useEffect(() => {
    if (!autoSeed || initRef.current || value) return;
    initRef.current = true;

    const profileVal = buildProfileValue();

    if (profileVal) {
      onChange(profileVal);
      checkDeviceLocation(profileVal);
    } else {
      getDeviceLocation((deviceVal) => {
        // Only autofill from device location when it resolves to a real street.
        // A coords-only or centroid result isn't precise enough to prefill the
        // listing pin — the user would just have to fix it.
        if (deviceVal && deviceVal.address.street1) onChange(deviceVal);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function buildProfileValue(): LocationPickerValue | null {
    return buildProfileLocation(profileAddress ?? null);
  }

  // Returns whatever the browser geolocation + reverse geocode yield. The
  // returned value is sufficient for a coord-based mismatch comparison; callers
  // that want to *autofill* the listing pin with this value should additionally
  // check `street1` (a city/ZIP centroid is not precise enough to prefill an
  // address field).
  function getDeviceLocation(cb: (val: LocationPickerValue | null) => void) {
    if (!navigator.geolocation) return cb(null);

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const addr = await reverseGeocodeRef.current({ lat: latitude, lon: longitude });
          cb({ address: addr, displayText: formatAddress(addr) });
        } catch {
          cb({
            address: { lat: latitude, lon: longitude },
            displayText: `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`,
          });
        }
      },
      () => cb(null),
    );
  }

  function useCurrentLocation(): Promise<void> {
    if (!navigator.geolocation) {
      return Promise.reject(new Error('Location is not available on this device.'));
    }

    return new Promise<void>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude } = pos.coords;
          try {
            const addr = await reverseGeocodeRef.current({ lat: latitude, lon: longitude });
            onChangeRef.current({ address: addr, displayText: formatAddress(addr) });
          } catch {
            onChangeRef.current({
              address: { lat: latitude, lon: longitude },
              displayText: `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`,
            });
          }
          resolve();
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) {
            reject(new Error('Location permission denied. Enable it in your browser or device settings.'));
          } else if (err.code === err.POSITION_UNAVAILABLE) {
            reject(new Error('Could not determine your location. Try again or enter an address.'));
          } else if (err.code === err.TIMEOUT) {
            reject(new Error('Location request timed out. Try again.'));
          } else {
            reject(new Error('Could not get your current location.'));
          }
        },
        // maximumAge: 0 — force a fresh fix. A cached position can outlive the
        // user turning device/OS location off and would silently autofill a
        // stale pin; a current action must use a current fix.
        { enableHighAccuracy: false, timeout: 10_000, maximumAge: 0 },
      );
    });
  }

  useImperativeHandle(ref, () => ({ useCurrentLocation }), []);

  function checkDeviceLocation(profileVal: LocationPickerValue) {
    getDeviceLocation((deviceVal) => {
      if (
        !deviceVal ||
        !deviceVal.address.lat ||
        !deviceVal.address.lon ||
        !profileVal.address.lat ||
        !profileVal.address.lon
      )
        return;

      const dist = distanceMiles(
        { lat: profileVal.address.lat, lon: profileVal.address.lon },
        { lat: deviceVal.address.lat, lon: deviceVal.address.lon },
      );

      if (dist > MISMATCH_THRESHOLD_MILES) {
        setConflict({ profile: profileVal, device: deviceVal });
      }
    });
  }

  function resolveConflict(choice: 'profile' | 'device') {
    if (!conflict) return;
    onChange(choice === 'profile' ? conflict.profile : conflict.device);
    setConflict(null);
  }

  // --- Map lifecycle ---
  useEffect(() => {
    if (!mapVisible) return;

    let cancelled = false;
    let createdMap: GoogleMap | null = null;

    const timeoutId = window.setTimeout(() => {
      if (cancelled) return;
      void create();
    }, 0);

    const create = async () => {
      if (!mapRef.current) return;
      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        console.warn('[LocationPicker] VITE_GOOGLE_MAPS_API_KEY is not set');
        return;
      }

      const current = valueRef.current;
      const initialCenter =
        current?.address.lat && current?.address.lon
          ? { lat: current.address.lat, lng: current.address.lon }
          : DEFAULT_CENTER;

      const map = await GoogleMap.create({
        id: `location-picker-${Date.now()}`,
        element: mapRef.current,
        apiKey,
        ...getBaseMapLocale(),
        config: {
          ...BASE_MAP_CONFIG,
          center: initialCenter,
          zoom: current?.address.lat ? zoomFor(current.address.lat) : 14,
        },
      });

      if (cancelled) {
        await map.destroy();
        return;
      }

      createdMap = map;
      instanceRef.current = map;

      if (current?.address.lat && current?.address.lon) {
        const id = await map.addMarker({
          coordinate: { lat: current.address.lat, lng: current.address.lon },
          draggable: true,
        });
        markerIdRef.current = id;
        await drawRadius(map, current.address.lat, current.address.lon);
      }

      await map.setOnMapClickListener((event) => {
        void handleMapPick(event.latitude, event.longitude, map);
      });

      await map.setOnMarkerDragEndListener((event) => {
        void handleMapPick(event.latitude, event.longitude);
      });
    };

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      if (createdMap) {
        createdMap.destroy();
        createdMap = null;
      }
      instanceRef.current = null;
      markerIdRef.current = null;
      circleIdsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapVisible]);

  // --- Sync map whenever value changes ---
  useEffect(() => {
    const map = instanceRef.current;
    if (!map || !value?.address.lat || !value?.address.lon) return;

    void syncMapToCoords(map, value.address.lat, value.address.lon);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.address.lat, value?.address.lon]);

  // --- Resize the drop-off circle as the radius changes ---
  useEffect(() => {
    const map = instanceRef.current;
    const current = valueRef.current;
    if (!map || !current?.address.lat || !current?.address.lon) return;
    const { lat, lon } = current.address;
    void (async () => {
      await drawRadius(map, lat, lon);
      await map.setCamera({ coordinate: { lat, lng: lon }, zoom: zoomFor(lat), animate: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radiusMiles]);

  // --- Autocomplete ---
  function handleSearchChange(text: string) {
    setSearchText(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (text.length < 3) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      autocompleteMutation.mutate(
        { searchText: text, sessionToken },
        {
          onSuccess: (result) => {
            setSessionToken(result.sessionToken);
            setSuggestions(result.suggestions);
          },
          onError: () => setSuggestions([]),
        },
      );
    }, 300);
  }

  function handleSelectSuggestion(suggestion: AddressSuggestion) {
    setSuggestions([]);
    setSearchText(suggestion.address);
    geocodeMutation.mutate(suggestion.address, {
      onSuccess: (addr) => {
        onChange({ address: addr, displayText: formatAddress(addr) });
      },
      onError: (err) => console.error('[LocationPicker] geocode failed', err),
    });
  }

  function handleApt2Change(text: string) {
    setApt2(text);
    if (apt2DebounceRef.current) clearTimeout(apt2DebounceRef.current);
    apt2DebounceRef.current = setTimeout(() => {
      const current = valueRef.current;
      if (!current) return;
      const trimmed = text.trim();
      const nextAddress: Address = {
        ...current.address,
        street2: trimmed || undefined,
        // Typing a unit implicitly contradicts a prior "no unit" answer.
        noUnit: trimmed ? false : current.address.noUnit,
      };
      onChangeRef.current({
        address: nextAddress,
        displayText: formatAddress(nextAddress),
      });
    }, 400);
  }

  function handleNoUnit() {
    const current = valueRef.current;
    if (!current) return;
    if (apt2DebounceRef.current) {
      clearTimeout(apt2DebounceRef.current);
      apt2DebounceRef.current = null;
    }
    setApt2('');
    const nextAddress: Address = {
      ...current.address,
      street2: undefined,
      noUnit: true,
    };
    onChangeRef.current({
      address: nextAddress,
      displayText: formatAddress(nextAddress),
    });
  }

  /** Flush any pending debounced apt2 emit immediately. */
  function commitApt2Now() {
    if (apt2DebounceRef.current) {
      clearTimeout(apt2DebounceRef.current);
      apt2DebounceRef.current = null;
    }
    const current = valueRef.current;
    if (!current) return;
    const trimmed = apt2.trim();
    if ((current.address.street2 ?? '') === trimmed) return;
    const nextAddress: Address = {
      ...current.address,
      street2: trimmed || undefined,
      noUnit: trimmed ? false : current.address.noUnit,
    };
    onChangeRef.current({
      address: nextAddress,
      displayText: formatAddress(nextAddress),
    });
  }

  function handleEditSave(addr: Address) {
    onChangeRef.current({
      address: addr,
      displayText: formatAddress(addr),
    });
    setIsEditing(false);
  }

  function handleEditCancel() {
    setIsEditing(false);
  }

  const hasSelectedAddress = !!value?.address.street1;
  const showAptVerify =
    hasSelectedAddress && !value?.address.street2 && !value?.address.noUnit;
  // Pin-only location: lat/lon present but no street address. User must
  // explicitly confirm before this counts as a valid location. Suppressed in
  // zip-friendly mode — a ZIP-level profile address doesn't need a pin.
  const showPinVerify =
    !hideMapAndPinForZip &&
    !!value &&
    !value.address.street1 &&
    value.address.lat != null &&
    value.address.lon != null &&
    !value.address.pinConfirmed;

  function handleConfirmPin() {
    const current = valueRef.current;
    if (!current) return;
    const nextAddress: Address = { ...current.address, pinConfirmed: true };
    onChangeRef.current({
      address: nextAddress,
      displayText: formatAddress(nextAddress),
    });
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Conflict prompt */}
      {conflict && (
        <div
          className="rounded-lg border p-3"
          style={{
            backgroundColor: 'var(--ion-color-warning-tint)',
            borderColor: 'var(--ion-color-warning-shade)',
            color: 'var(--ion-color-warning-contrast)',
          }}
        >
          <p className="mb-2 text-sm font-medium">
            Your current location doesn't match your profile address. Which
            would you like to use?
          </p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => resolveConflict('profile')}
              className="w-full rounded-lg border px-3 py-2 text-left text-sm hover:bg-surface-50"
              style={{
                backgroundColor: 'var(--ion-background-color)',
                borderColor: 'var(--ion-color-medium-tint)',
                color: 'var(--ion-text-color)',
              }}
            >
              <span className="font-medium">Profile address</span>
              <br />
              <span style={{ color: 'var(--ion-text-color-step-300)' }}>
                {conflict.profile.displayText}
              </span>
            </button>
            <button
              type="button"
              onClick={() => resolveConflict('device')}
              className="w-full rounded-lg border px-3 py-2 text-left text-sm hover:bg-surface-50"
              style={{
                backgroundColor: 'var(--ion-background-color)',
                borderColor: 'var(--ion-color-medium-tint)',
                color: 'var(--ion-text-color)',
              }}
            >
              <span className="font-medium">Current location</span>
              <br />
              <span style={{ color: 'var(--ion-text-color-step-300)' }}>
                {conflict.device.displayText}
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Pin-only location verify row — shown when value has lat/lon but no
          street1, and the user hasn't yet confirmed. Sits above the address
          display. Disappears once confirmed; reappears if the pin moves. */}
      {showPinVerify && (
        <div
          className="flex flex-col gap-2 rounded-lg border p-3"
          style={{
            backgroundColor: 'var(--ion-color-warning-tint)',
            borderColor: 'var(--ion-color-warning-shade)',
            color: 'var(--ion-color-warning-contrast)',
          }}
        >
          <p className="text-sm font-medium">
            This pin doesn't match a street address. Confirm the location is correct.
          </p>
          <button
            type="button"
            onClick={handleConfirmPin}
            className="self-start rounded-lg border px-3 py-1.5 text-sm font-medium"
            style={{
              backgroundColor: 'var(--ion-background-color)',
              borderColor: 'var(--ion-color-medium-tint)',
              color: 'var(--ion-text-color)',
            }}
          >
            Confirm pin location
          </button>
        </div>
      )}

      {/* Default address input: three modes —
          (a) editing → inline AddressEditCard (replaces the default input)
          (b) value exists (street or pin) → read-only display + pencil
          (c) no value → autocomplete search + pencil (for manual entry) */}
      {isEditing ? (
        <AddressEditCard
          value={value?.address ?? null}
          onSave={handleEditSave}
          onCancel={handleEditCancel}
        />
      ) : value ? (
        <div
          className="flex items-center justify-between gap-2 rounded-lg border px-3 py-2"
          style={{
            backgroundColor: 'var(--ion-background-color)',
            borderColor: 'var(--ion-color-medium-tint)',
          }}
        >
          <span
            className="text-sm"
            style={{ color: 'var(--ion-text-color)' }}
          >
            {value.displayText}
          </span>
          <button
            type="button"
            aria-label={t('locationPicker.editAria')}
            onClick={() => setIsEditing(true)}
            className="shrink-0 rounded p-1"
            style={{ color: 'var(--ion-color-primary)' }}
          >
            <Pencil size={16} />
          </button>
        </div>
      ) : (
        <div ref={searchWrapperRef} className="relative">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setSuggestions([]);
              }}
              placeholder={t('locationPicker.searchPlaceholder')}
              className="w-full rounded-lg border px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--ion-color-primary)]"
              style={{
                backgroundColor: 'var(--ion-background-color)',
                color: 'var(--ion-text-color)',
                borderColor: 'var(--ion-color-medium-tint)',
              }}
            />
            <button
              type="button"
              aria-label={t('locationPicker.manualEntryAria')}
              onClick={() => setIsEditing(true)}
              className="shrink-0 rounded p-1"
              style={{ color: 'var(--ion-color-primary)' }}
            >
              <Pencil size={16} />
            </button>
          </div>
          {suggestions.length > 0 && (
            <div
              className="absolute z-10 mt-1 max-h-40 w-full overflow-y-auto rounded-lg border shadow-lg"
              style={{
                backgroundColor: 'var(--ion-background-color)',
                borderColor: 'var(--ion-text-color-step-800)',
              }}
            >
              {suggestions.map((s) => (
                <button
                  key={s.placeId}
                  type="button"
                  onClick={() => handleSelectSuggestion(s)}
                  className="w-full border-b px-3 py-2 text-left text-sm hover:bg-surface-50 last:border-b-0"
                  style={{
                    color: 'var(--ion-text-color)',
                    borderBottomColor: 'var(--ion-text-color-step-800)',
                  }}
                >
                  {s.address}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Apt/unit verify row — only when an address is set, street2 is empty,
          and the user hasn't explicitly dismissed with "None". The label sits
          on its own line above the input + "None" so the row never overflows at
          large text sizes (input shrinks via min-w-0, button stays). */}
      {!isEditing && showAptVerify && (
        <div
          className="flex flex-col gap-2 rounded-lg border px-3 py-2"
          style={{
            backgroundColor: 'var(--ion-background-color)',
            borderColor: 'var(--ion-color-warning-shade)',
          }}
        >
          <span
            className="text-sm"
            style={{ color: 'var(--ion-text-color)' }}
          >
            {t('picker.aptPrompt')}
          </span>
          <div className="flex gap-2">
            <input
              type="text"
              value={apt2}
              onChange={(e) => handleApt2Change(e.target.value)}
              onBlur={commitApt2Now}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitApt2Now();
                }
              }}
              placeholder={t('picker.aptExample')}
              className="min-w-0 flex-1 rounded-lg border px-2 py-1 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--ion-color-primary)]"
              style={{
                backgroundColor: 'var(--ion-background-color)',
                color: 'var(--ion-text-color)',
                borderColor: 'var(--ion-color-medium-tint)',
              }}
            />
            <button
              type="button"
              onClick={handleNoUnit}
              className="shrink-0 rounded-lg border px-3 py-1 text-sm"
              style={{
                backgroundColor: 'var(--ion-background-color)',
                borderColor: 'var(--ion-color-medium-tint)',
                color: 'var(--ion-text-color)',
              }}
            >
              {t('addressEdit.noUnit')}
            </button>
          </div>
        </div>
      )}

      {/* Interactive map — only rendered once we have coords to center on. */}
      {mapVisible && (
        <>
          <div
            ref={mapRef}
            className="h-48 w-full overflow-hidden rounded-lg bg-surface-100"
            style={{ touchAction: 'none' }}
          />
          <p
            className="text-xs"
            style={{ color: 'var(--ion-text-color-step-400)' }}
          >
            {t('picker.mapHint')}
          </p>
        </>
      )}
    </div>
  );
});

export function formatAddress(addr: Address): string {
  const parts = [
    addr.street1,
    addr.street2,
    addr.city,
    addr.stateProvince,
    addr.postalCode,
  ].filter(Boolean);
  return parts.join(', ') || addr.address || '';
}

// Returns a seed value when the profile has either a real street address OR
// an explicitly-confirmed pin location with coords. A ZIP-only profile
// (postal/country/lat/lon scalars without a `type:'profile'` address — or a
// `type:'profile'` address that's just an unconfirmed centroid) yields null
// so the listing's location field stays empty rather than auto-filling
// imprecise data the user would have to fix.
export function buildProfileLocation(
  profileAddress: Address | null,
): LocationPickerValue | null {
  const hasUsableLocation =
    !!profileAddress &&
    (!!profileAddress.street1 || !!profileAddress.pinConfirmed) &&
    profileAddress.lat != null &&
    profileAddress.lon != null;
  if (hasUsableLocation) {
    return {
      address: profileAddress,
      displayText: formatAddress(profileAddress),
    };
  }
  return null;
}

// Same usability check as buildProfileLocation, but for a listing's own saved
// `listingLocation` (returned by the API only to the listing's owner). Used to
// seed the picker on edit so a previously-saved listing address is restored
// instead of falling back to the profile address.
export function buildListingLocation(
  listingAddress: Address | null | undefined,
): LocationPickerValue | null {
  if (!listingAddress) return null;
  const hasUsableLocation =
    (!!listingAddress.street1 || !!listingAddress.pinConfirmed) &&
    listingAddress.lat != null &&
    listingAddress.lon != null;
  if (hasUsableLocation) {
    return {
      address: listingAddress,
      displayText: formatAddress(listingAddress),
    };
  }
  return null;
}
